import { useEffect, useRef, useState } from "react";

import { playPairFailureSound, playPairSuccessSound } from "@/lib/floorStationPairAudio";
import { cn } from "@/lib/utils";

export type DeferredCommitFlashTone = "success" | "error" | null;

export type UseDeferredCommitFieldFeedbackOptions = {
  /** When false, success and error tones are skipped (visual flash still applies). Default true. */
  playAudio?: boolean;
  /**
   * Stable id for this field so feedback does not replay after unmount (for example tab switch).
   * Cleared when commitSucceeded becomes false or commitError is cleared.
   */
  feedbackId?: string;
};

const playedDeferredCommitFeedbackKeys = new Set<string>();

function deferredCommitFeedbackKey(feedbackId: string, tone: "success" | "error"): string {
  return `${feedbackId}:${tone}`;
}

function markDeferredCommitFeedbackPlayed(feedbackId: string, tone: "success" | "error"): void {
  playedDeferredCommitFeedbackKeys.add(deferredCommitFeedbackKey(feedbackId, tone));
}

function clearDeferredCommitFeedbackPlayed(feedbackId: string, tone: "success" | "error"): void {
  playedDeferredCommitFeedbackKeys.delete(deferredCommitFeedbackKey(feedbackId, tone));
}

function deferredCommitFeedbackAlreadyPlayed(feedbackId: string, tone: "success" | "error"): boolean {
  return playedDeferredCommitFeedbackKeys.has(deferredCommitFeedbackKey(feedbackId, tone));
}

/** Play hub success/failure tones and brief field flash when a deferred commit settles. */
export function useDeferredCommitFieldFeedback(
  commitSucceeded?: boolean,
  commitError?: string | null,
  options?: UseDeferredCommitFieldFeedbackOptions,
): DeferredCommitFlashTone {
  const [flashTone, setFlashTone] = useState<DeferredCommitFlashTone>(null);
  const sawSuccessRef = useRef(false);
  const lastErrorRef = useRef("");
  const playAudio = options?.playAudio !== false;
  const feedbackId = options?.feedbackId;

  useEffect(() => {
    if (commitSucceeded === true && !sawSuccessRef.current) {
      sawSuccessRef.current = true;
      if (
        feedbackId != null &&
        deferredCommitFeedbackAlreadyPlayed(feedbackId, "success")
      ) {
        return undefined;
      }
      if (feedbackId != null) {
        markDeferredCommitFeedbackPlayed(feedbackId, "success");
      }
      if (playAudio) {
        playPairSuccessSound();
      }
      setFlashTone("success");
      const timer = window.setTimeout(() => setFlashTone(null), 1400);
      return () => window.clearTimeout(timer);
    }
    if (commitSucceeded !== true) {
      sawSuccessRef.current = false;
      if (feedbackId != null) {
        clearDeferredCommitFeedbackPlayed(feedbackId, "success");
      }
    }
    return undefined;
  }, [commitSucceeded, feedbackId, playAudio]);

  useEffect(() => {
    const message = commitError?.trim() ?? "";
    if (message !== "" && message !== lastErrorRef.current) {
      lastErrorRef.current = message;
      if (
        feedbackId != null &&
        deferredCommitFeedbackAlreadyPlayed(feedbackId, "error")
      ) {
        return undefined;
      }
      if (feedbackId != null) {
        markDeferredCommitFeedbackPlayed(feedbackId, "error");
      }
      if (playAudio) {
        playPairFailureSound();
      }
    }
    if (message === "") {
      lastErrorRef.current = "";
      if (feedbackId != null) {
        clearDeferredCommitFeedbackPlayed(feedbackId, "error");
      }
    }
    return undefined;
  }, [commitError, feedbackId, playAudio]);

  return flashTone;
}

export function deferredCommitFlashClassName(flashTone: DeferredCommitFlashTone): string {
  if (flashTone === "success") {
    return "border-emerald-500 bg-emerald-100 ring-2 ring-emerald-300 transition-colors duration-300";
  }
  if (flashTone === "error") {
    return "border-rose-500 bg-rose-100 ring-2 ring-rose-300 transition-colors duration-300";
  }
  return "";
}

export function deferredCommitDirtyClassName(isDirty: boolean, disabled: boolean): string {
  return cn(
    isDirty &&
      !disabled &&
      "border-amber-400 bg-amber-50 focus:border-amber-500 focus:ring-amber-500",
  );
}

export function deferredCommitErrorVisible(commitError: string | null | undefined, isDirty: boolean): boolean {
  return (commitError?.trim() ?? "") !== "" && !isDirty;
}

/** Yellow while unsaved, green flash on success, red until the value changes. */
export function deferredCommitControlClassName({
  isDirty,
  disabled,
  pending,
  commitError,
  flashTone,
}: {
  isDirty: boolean;
  disabled: boolean;
  pending?: boolean;
  commitError?: string | null;
  flashTone: DeferredCommitFlashTone;
}): string {
  const errorVisible = deferredCommitErrorVisible(commitError, isDirty) && pending !== true;
  return cn(
    pending === true && "pr-8 opacity-70",
    errorVisible && "border-rose-500 bg-rose-100 pr-8 ring-2 ring-rose-300",
    !errorVisible && pending !== true && deferredCommitDirtyClassName(isDirty, disabled),
    !errorVisible && deferredCommitFlashClassName(flashTone === "error" ? null : flashTone),
  );
}

export function deferredCommitOverlayGlyphPadding(showOverlayGlyph: boolean): string {
  return showOverlayGlyph ? "pr-8" : "";
}
